import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
} from "reactstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { AiFillEdit } from "react-icons/ai";
import { MdDeleteOutline } from "react-icons/md";
import PageContainer from "../Layout/PageContainer";
import {
  fetchSubSpaces,
  createSubSpace,
  updateSubSpace,
  deleteSubSpace as deleteSubSpaceAction,
} from "../../redux/actions/subSpaces";
import debounceFunction from "../../helpers/Debounce";
import IconContainer from "../Common/IconContainer";

toast.configure();

const SubSpaces = () => {
  const dispatch = useDispatch();
  const subspaces = useSelector((state) => state.subSpacesReducer.subspaces);
  const totalSubSpaceCount = useSelector(
    (state) => state.subSpacesReducer.totalSubSpaceCount
  );

  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState("");
  const [filteredSubspaces, setFilteredSubspaces] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [addSubSpace, setAddSubSpace] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSubSpace, setSelectedSubSpace] = useState(null);

  // Amenity options
  const amenityOptions = [
    { id: 1, name: 'Indoor' },
    { id: 2, name: 'Outdoor' },
    { id: 3, name: 'Climate Controlled' },
    { id: 4, name: 'Smoke Free' },
    { id: 5, name: 'Smoke Detectors' },
    { id: 6, name: 'Private Entrance' },
    { id: 7, name: 'Private Space' },
    { id: 8, name: 'Locked Area' },
    { id: 9, name: 'Pet Free' },
    { id: 10, name: 'Security Camera Monitored' },
    { id: 11, name: 'No Stairs' },
    { id: 12, name: 'Lighting' },
    { id: 13, name: 'Ventilation Facility' },
    { id: 14, name: 'Pickup and Drop Services by Host' },
    { id: 15, name: 'Alarm' },
    { id: 16, name: 'Gated' },
    { id: 17, name: 'EV charging' },
    { id: 18, name: 'Bathroom' },
    { id: 19, name: 'Electric outlets' },
    { id: 20, name: 'Fence' },
    { id: 21, name: 'Trash Area' },
    { id: 22, name: 'Wash Area' },
    { id: 23, name: 'Ice Machine' },
    { id: 24, name: 'Roll-up Door' },
    { id: 25, name: 'Drive-up Access' },
    { id: 26, name: 'Access Code' },
    { id: 27, name: 'Handcarts' },
    { id: 29, name: 'On-site Manager' },
    { id: 30, name: 'Handicap Access Units' }
  ];

  // Fetch subspaces when the component mounts
  useEffect(() => {
    dispatch(fetchSubSpaces());
  }, [dispatch]);

  // Comprehensive search function
  const performSearch = useCallback((searchText) => {
    if (!searchText) return subspaces;

    const lowercaseSearch = searchText.toLowerCase();

    return subspaces.filter((subspace) => {
      // Safely convert to string and lowercase
      const safeToString = (value) => {
        if (value === null || value === undefined) return '';
        return String(value).toLowerCase();
      };

      // Search across multiple fields
      const searchFields = [
        safeToString(subspace.id),
        safeToString(subspace.listingId),
        safeToString(subspace.quantity),
        safeToString(subspace.weeklyRate),
        safeToString(subspace.monthlyRate),
        `${safeToString(subspace.subSpaceWidth)}x${safeToString(subspace.subSpaceHeight)}`,
        // Check amenities
        subspace.amenities && subspace.amenities.length > 0
          ? subspace.amenities.some((amenity) =>
            safeToString(amenity.name).includes(lowercaseSearch)
          )
          : false
      ];

      // Check if any field matches the search text
      return searchFields.some((field) =>
        safeToString(field).includes(lowercaseSearch)
      );
    });
  }, [subspaces]);

  // Search effect
  useEffect(() => {
    const results = performSearch(filterText);
    setFilteredSubspaces(results);
  }, [filterText, performSearch]);

  // Debounced search input handler
  const handleSearchChange = useCallback(
    debounceFunction((value) => {
      setFilterText(value);
    }, 300),
    []
  );

  // Handler for editing a sub space
  const handleEditClick = (subspace) => {
    setSelectedSubSpace({
      ...subspace,
      amenities: subspace.amenities?.map((amenity) =>
        typeof amenity === "object" ? amenity.id : amenity
      ) || [],
    });
    setModalOpen(true);
  };

  // Close modal handlers
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSubSpace(null);
  };

  const handleCloseAddSubSpace = () => {
    setAddSubSpace(false);
    setSelectedSubSpace(null);
  };

  // Delete sub space handler
  const handleDeleteSubSpace = (subspace) => {
    setSelectedSubSpace(subspace.id);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteSubSpace = () => {
    setIsDeleteModalOpen(false);
    setSelectedSubSpace(null);
  };



  const handleSubmitUpdateSubSpace = () => {
    if (!selectedSubSpace?.id) {
      toast.error("Error: Sub Space ID is missing!");
      return;
    }

    dispatch(updateSubSpace(selectedSubSpace))
      .then(() => {
        dispatch(fetchSubSpaces());
        setModalOpen(false);
        setSelectedSubSpace(null);
        toast.success("Sub Space updated successfully!");
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to update Sub Space.");
      });
  };

  const handleSubmitDeleteSubSpace = () => {
    if (!selectedSubSpace) {
      toast.error("Error: No Sub Space selected to delete!");
      return;
    }

    dispatch(deleteSubSpaceAction(selectedSubSpace))
      .then(() => {
        dispatch(fetchSubSpaces());
        setIsDeleteModalOpen(false);
        setSelectedSubSpace(null);
        toast.success("Sub Space deleted successfully!");
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to delete Sub Space.");
      });
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        name: "Sub Space ID",
        selector: (row) => row.id,
        sortable: true,
      },
      {
        name: "Listing ID",
        selector: (row) => row.listingId,
        sortable: true,
      },
      {
        name: "Listing Title",
        selector: (row) => `${row.space.title}`,
        sortable: false,
        style: {
          whiteSpace: "pre-wrap",
          wordWrap: "wrap",
          width: "500px",
        }
      },
      {
        name: "Size",
        selector: (row) => `${row.subSpaceWidth} x ${row.subSpaceLength} x ${row.subSpaceHeight}`,
        sortable: false,
      },
      {
        name: "Quantity",
        selector: (row) => row.quantity,
        sortable: false,
      },
      {
        name: "Weekly Rate",
        selector: (row) => `$${row.weeklyRate}`,
        sortable: false,
      },
      {
        name: "Monthly Rate",
        selector: (row) => `$${row.monthlyRate}`,
        sortable: false,
      },
      {
        name: "Amenities",
        selector: (row) =>
          row.amenities ? row.amenities.map((amenity) => amenity.name).join(", ") : "",
        sortable: false,
      },
      {
        name: "Actions",
        button: true,
        cell: (row) => (
          <>
            <IconContainer
              Icon={AiFillEdit}
              handleOnClick={() => handleEditClick(row)}
              text="Edit"
            />
            <IconContainer
              Icon={MdDeleteOutline}
              handleOnClick={() => handleDeleteSubSpace(row)}
              text="Delete"
            />
          </>
        ),
      },
    ],
    []
  );

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(fetchSubSpaces(filterText));
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    dispatch(fetchSubSpaces(filterText));
  };

  return (
    <PageContainer
      pageTitleIcon="pe-7s-home icon-gradient bg-plum-plate"
      pageHeading="Sub Spaces"
      pageSubTitle="Listing all the sub spaces"
    >
      {/* Search Input */}
      <Input
        type="text"
        placeholder="Search Sub Spaces (ID, Size, Rates, Amenities)"
        className="mb-3"
        onChange={(e) => handleSearchChange(e.target.value)}
      />

      {/* Edit Modal */}
      <Modal isOpen={modalOpen} toggle={handleCloseModal}>
        <ModalHeader toggle={handleCloseModal}>Edit Sub Space</ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-md-6">
              <Label>Width</Label>
              <Input
                type="number"
                placeholder="Width"
                value={selectedSubSpace?.subSpaceWidth || ""}
                onChange={(e) =>
                  setSelectedSubSpace((prev) => ({
                    ...prev,
                    subSpaceWidth: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-6">
              <Label className="">Length</Label>
              <Input
                type="number"
                placeholder="Length"
                value={selectedSubSpace?.subSpaceLength || ""}
                onChange={(e) =>
                  setSelectedSubSpace((prev) => ({
                    ...prev,
                    subSpaceLength: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <Label className="mt-3">Height</Label>
              <Input
                type="number"
                placeholder="Height"
                value={selectedSubSpace?.subSpaceHeight || ""}
                onChange={(e) =>
                  setSelectedSubSpace((prev) => ({
                    ...prev,
                    subSpaceHeight: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-6">
              <Label className="mt-3">Quantity</Label>
              <Input
                type="number"
                placeholder="Quantity"
                value={selectedSubSpace?.quantity || ""}
                onChange={(e) =>
                  setSelectedSubSpace((prev) => ({
                    ...prev,
                    quantity: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <Label className="mt-3">Weekly Rate</Label>
              <Input
                type="number"
                placeholder="Weekly Rate"
                value={selectedSubSpace?.weeklyRate || ""}
                onChange={(e) =>
                  setSelectedSubSpace((prev) => ({
                    ...prev,
                    weeklyRate: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-6">
              <Label className="mt-3">Monthly Rate</Label>
              <Input
                type="number"
                placeholder="Monthly Rate"
                value={selectedSubSpace?.monthlyRate || ""}
                onChange={(e) =>
                  setSelectedSubSpace((prev) => ({
                    ...prev,
                    monthlyRate: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <Label className="mt-3">Amenities</Label>
          <Select
            isMulti
            options={amenityOptions.map((option) => ({
              value: option.id,
              label: option.name,
            }))}
            value={selectedSubSpace?.amenities?.map((amenity) => ({
              value: amenity.id || amenity,
              label:
                amenityOptions.find((option) => option.id === (amenity.id || amenity))
                  ?.name || "",
            }))}
            onChange={(selectedOptions) =>
              setSelectedSubSpace((prev) => ({
                ...prev,
                amenities: selectedOptions.map((option) => option.value),
              }))
            }
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSubmitUpdateSubSpace}>
            Save Changes
          </Button>
          <Button color="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteModalOpen} toggle={handleCloseDeleteSubSpace} >
        <ModalHeader toggle={handleCloseDeleteSubSpace}>Delete Sub Space</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this sub space?
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleSubmitDeleteSubSpace}>
            Delete
          </Button>
          <Button color="secondary" onClick={handleCloseDeleteSubSpace}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal >

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={filteredSubspaces.length > 0 ? filteredSubspaces : subspaces}
        pagination
        paginationServer
        paginationTotalRows={totalSubSpaceCount}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        subHeader
        persistTableHead
      />
    </PageContainer>
  );}
  export default SubSpaces;